<template>
  <Modal
    v-bind="getBindValue"
    ref="modal"
    :open="props.open"
    :title="props.title"
    :keyboard="keyboard"
    :wrap-class-name="`${bem.b()} ${props.wrapClassName} ${currentClass}`"
    :class="[
      props.class,
      hasScroll ? bem.is('has-scroll') : '',
      bem.is(props.isConfirmModel ? 'confirm-model' : ''),
    ]"
    @cancel="handleCancel"
    @ok="handleOk"
  >
    <div v-if="$slots.step" :class="bem.e('step')">
      <slot name="step" />
    </div>
    <div :class="[bem.e('container'), symbolClass, bem.is(props.type)]">
      <div v-if="hasScroll && scrollTop > 0" :class="bem.e('shadow')" />
      <div :class="[symbolComparedClass]">
        <slot />
      </div>
    </div>
    <template v-if="props.showFooter" #footer>
      <slot name="footer">
        <!-- 确认框 -->
        <div :class="bem.e('footer')">
          <ll-button
            v-if="props.showCancel && props.type !== 'confirm'"
            :label="props.cancelText ? props.cancelText : '取消'"
            size="middle"
            @click="handleCancel"
          >
          </ll-button>
          <ll-button
            type="primary"
            :loading="props.loading"
            size="middle"
            :label="
              props.confirmText
                ? props.confirmText
                : props.type === 'confirm'
                ? '知道了'
                : '确认'
            "
            @click="handleOk"
          />
        </div>
      </slot>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { useAttrs, ref, watch, nextTick, computed } from 'vue'
import { Modal } from 'ant-design-vue'
import { LlButton } from '@ll-plus/components'
import { createNamespace } from '@ll-plus/utils'
import { omit } from 'lodash-es'
import { modalProps, modalEmits } from './modal'
import type { IResultType } from './modal'
defineOptions({ name: 'LlModal' })
// 1.props/ref
const props = defineProps(modalProps)
// 2.emits
const emits = defineEmits(modalEmits)

const attrs = useAttrs()

const bem = createNamespace('modal')

const modal = ref<HTMLElement>()

const hasScroll = ref(false)

const scrollTop = ref(0)

const currentClass = ref<string>('')

const symbolClass = ref(`ll-modal-${new Date().getTime()}`)

const symbolComparedClass = ref(`model-content-${new Date().getTime()}`)

const getBindValue = computed(() => {
  const result: IResultType = {
    ...omit({ ...attrs }, [...Object.keys(modalProps), 'wrapClassName']),
  }
  if (props.showFooter === false) {
    result.footer = null
  }
  return result
})

// 3.methods
const handleCancel = () => {
  emits('cancel')
  emits('update:open')
}
const handleOk = () => {
  emits('ok')
}

const handleResize = async (value: boolean) => {
  await nextTick()
  const ele = document.getElementsByClassName(symbolClass.value)[0]
  const modalBody = ele.getElementsByClassName(symbolComparedClass.value)[0]
  const resizeObserver = new ResizeObserver((entries) => {
    // 处理大小变化的回调函数
    const { clientHeight } = entries[0].target
    const scrollHeight = ele.clientHeight
    hasScroll.value = clientHeight > scrollHeight
  })
  if (value) {
    resizeObserver.observe(modalBody)
    ele.addEventListener('scroll', () => {
      scrollTop.value = ele.scrollTop
    })
  } else {
    resizeObserver.unobserve(modalBody)
    ele.removeEventListener('scroll', () => {
      scrollTop.value = 0
    })
  }
}

const onScrollTop = async () => {
  await nextTick()
  if (!hasScroll.value) return
  const ele = document.getElementsByClassName(symbolClass.value)[0]
  ele.scrollTop = 0
}

// 4.watch/computed
watch(
  () => props.open,
  (val) => {
    handleResize(val)
    if (val) {
      currentClass.value = `modal-${new Date().getTime()}`
      nextTick().then(() => {
        const dom = document.querySelector(
          `.${currentClass.value}`
        ) as HTMLElement
        if (dom) {
          dom.focus()
        }
      })
    }
  }
)

defineExpose({
  onScrollTop,
})
</script>
